/**
 * Вигляд екрана сутності з воронкою (KAN-01): «Воронка» (дошка, `/b/<подання>`) чи «Список»
 * (`/l/<подання>`). Перемикач — сегменти `role="group"` з `aria-pressed` (components.md, розд. 6).
 * Вибір пам'ятається для людини й екрана (`prefs.viewLayouts`, ключ — сутність), тож наступний
 * вхід у список цієї сутності відкриває дошку (`listRoute` у `app/routes/platform.ts`).
 *
 * Подання (а з ним і умова «мої / усі») переходить між виглядами без змін: дошка й список — два
 * вигляди того самого подання. Модуль — у першому екрані, тому тут лише перемикач і запис вибору,
 * без коду дошки.
 */
import type { MetaEntity } from '@clientline/contracts';
import { useNavigate } from '@tanstack/react-router';
import { viewSwitch as t } from '../i18n/uk/view-switch.ts';
import { getPrefs, setPrefs, VIEW_LAYOUTS_MAX } from '../prefs/prefs.ts';
import { hasBoard } from './pipeline.ts';

export type ViewLayout = 'board' | 'list';

/** Запам'ятований вигляд сутності; `undefined` — людина ще не обирала. */
export function layoutOf(entries: readonly string[], entity: string): ViewLayout | undefined {
  const entry = entries.find((e) => e.startsWith(`${entity}=`));
  const value = entry?.slice(entity.length + 1);
  return value === 'board' || value === 'list' ? value : undefined;
}

/** Записи з новим вибором сутності: він — у кінці, понад межу найстаріші відкидаються. */
export function withLayout(
  entries: readonly string[],
  entity: string,
  layout: ViewLayout,
  max = VIEW_LAYOUTS_MAX,
): string[] {
  return [...entries.filter((e) => !e.startsWith(`${entity}=`)), `${entity}=${layout}`].slice(-max);
}

export function rememberLayout(entity: string, layout: ViewLayout): void {
  const current = getPrefs().viewLayouts;
  if (layoutOf(current, entity) === layout) return;
  setPrefs({ viewLayouts: withLayout(current, entity, layout) });
}

/** Чи відкривати подання дошкою: сутність має воронку, а людина востаннє обрала «Воронку». */
export const opensAsBoard = (entity: MetaEntity, entries: readonly string[]): boolean =>
  layoutOf(entries, entity.key) === 'board' && hasBoard(entity);

/** Перемикач у панелі екрана; без воронки в сутності його немає. */
export function ViewSwitch({
  view,
  entity,
  current,
  draft,
  state,
}: {
  view: string;
  entity: MetaEntity;
  current: ViewLayout;
  /** Чернетка подання з адреси (`?d=`, view-draft.ts): умова й сортування йдуть в інший вигляд. */
  draft?: string | undefined;
  /** Ключ стану на сервері (`?state=`, view-state.ts): чернетка, що не йде в адресу, — теж. */
  state?: string | undefined;
}) {
  const navigate = useNavigate();
  if (!hasBoard(entity)) return null;
  const choose = (layout: ViewLayout) => {
    rememberLayout(entity.key, layout);
    if (layout === current) return;
    const search = state ? { state } : draft ? { d: draft } : {};
    void navigate(
      layout === 'board'
        ? { to: '/b/$view', params: { view }, search }
        : { to: '/l/$view', params: { view }, search },
    );
  };
  return (
    // biome-ignore lint/a11y/useSemanticElements: сегменти перемикача (components.md, розд. 6), не поле форми
    <div
      className="seg layout-switch"
      role="group"
      aria-label={t.group}
      data-testid="layout-switch"
    >
      <button
        type="button"
        aria-pressed={current === 'board'}
        onClick={() => choose('board')}
        data-testid="layout-switch-board"
        // На вузькій області підпис сховано — підказка з тим самим текстом (components.md, розд. 1).
        title={t.board}
      >
        <i className="ic ic-kanban" aria-hidden="true" />
        <span className="layout-switch__label">{t.board}</span>
      </button>
      <button
        type="button"
        aria-pressed={current === 'list'}
        onClick={() => choose('list')}
        data-testid="layout-switch-list"
        title={t.list}
      >
        <i className="ic ic-list" aria-hidden="true" />
        <span className="layout-switch__label">{t.list}</span>
      </button>
    </div>
  );
}
